<template>
    <Head title="Settings" />

    <TailwindLayout title="Settings">
        <div class="max-w-2xl">
            <h1 class="text-2xl font-bold text-gray-900 mb-2">Settings</h1>

            <p class="text-sm text-gray-600 mb-6">
                Set standard rules across all properties and applications.
            </p>

            <div
                v-if="user_global_auto_eso?.payment_method_required_but_missing"
                class="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
                You have no card on file, so landlord-paid automatic enhanced screening is paused. Add a payment method to resume.
            </div>

            <div
                v-if="$page.props.errors?.user_auto_eso"
                class="mb-6 rounded-md bg-red-50 p-4 text-sm text-red-800"
            >
                {{ $page.props.errors.user_auto_eso }}
            </div>

            <form class="space-y-6" @submit.prevent="submit">
                <div class="space-y-8 bg-white shadow sm:rounded-lg p-6 border border-gray-200">
                    <div>
                        <label for="minimum_credit_score" class="block text-sm font-semibold text-gray-900 mb-2">
                            Credit
                        </label>
                        <p class="text-sm text-gray-600 mb-2">
                            Consider scores at or above
                            <input
                                id="minimum_credit_score"
                                v-model="form.minimum_credit_score"
                                type="number"
                                min="300"
                                max="850"
                                placeholder="e.g. 620"
                                class="mx-1 inline-block w-28 rounded-md border-gray-300 shadow-sm placeholder:text-gray-400 focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                            />
                            (minimum credit score).
                        </p>
                        <p v-if="form.errors.minimum_credit_score" class="mt-1 text-sm text-red-600">
                            {{ form.errors.minimum_credit_score }}
                        </p>
                    </div>

                    <div>
                        <label for="rent_income_multiplier" class="block text-sm font-semibold text-gray-900 mb-2">
                            Income
                        </label>
                        <p class="text-sm text-gray-600 mb-2 flex flex-wrap items-center gap-1">
                            Applicant monthly income should be at least
                            <select
                                id="rent_income_multiplier"
                                v-model="form.rent_income_multiplier"
                                class="rounded-md border-gray-300 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                            >
                                <option value=""></option>
                                <option v-for="opt in multiplier_options" :key="opt" :value="opt">
                                    {{ opt }}
                                </option>
                            </select>
                            times the property&apos;s monthly rent.
                        </p>
                        <p v-if="form.errors.rent_income_multiplier" class="mt-1 text-sm text-red-600">
                            {{ form.errors.rent_income_multiplier }}
                        </p>
                    </div>

                    <!-- 86bc70q8c: account-wide auto-ESO, moved here from the Properties
                         list. Changing it opens the same enable/disable flow, which saves
                         on its own; the select only ever shows the saved state. -->
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                        <div>
                            <label for="anti_fraud_reports" class="block text-sm font-semibold text-gray-900 mb-1">
                                Anti-Fraud Reports
                            </label>
                            <p class="text-sm text-gray-600">
                                Enable enhanced screening for every application. This adds verified identity, income, employment and asset reports to your standard background check to reduce application fraud.
                            </p>
                        </div>
                        <select
                            id="anti_fraud_reports"
                            :value="antiFraudReportsValue"
                            class="w-24 shrink-0 rounded-md border-gray-300 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                            @change="onAntiFraudReportsChange"
                        >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>
                </div>

                <div>
                    <button
                        type="submit"
                        class="inline-flex justify-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rent-risk-blue-400 disabled:opacity-50"
                        :disabled="form.processing"
                    >
                        Save Settings
                    </button>
                </div>
            </form>
        </div>

        <UserLevelAutoEsoFlow
            ref="userAutoEsoFlowRef"
            :user-global-auto-eso="user_global_auto_eso"
            :is_agent_payment_method_added="is_agent_payment_method_added"
            :user_payment_method="user_payment_method"
            :stripe_data="stripe_data"
            preserve-state
        />
    </TailwindLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Head, useForm } from '@inertiajs/vue3'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import UserLevelAutoEsoFlow from '@/Components/UserLevelAutoEsoFlow.vue'

const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    rule: {
        type: Object,
        required: true,
    },
    multiplier_options: {
        type: Array,
        default: () => [],
    },
    user_global_auto_eso: {
        type: Object,
        default: () => ({
            is_active: false,
            payer: null,
            effective_billing: false,
            payment_method_required_but_missing: false,
        }),
    },
    is_agent_payment_method_added: { type: Boolean, default: false },
    user_payment_method: { type: Object, default: null },
    stripe_data: { type: [Object, Array], default: () => [] },
})

const userAutoEsoFlowRef = ref(null)

const antiFraudReportsValue = computed(() => {
    const eso = props.user_global_auto_eso
    return Boolean(eso?.is_active) || eso?.payer != null ? 'yes' : 'no'
})

function onAntiFraudReportsChange(event) {
    const chosen = event.target.value
    // Snap back to the saved state; the flow's save reloads the page props.
    event.target.value = antiFraudReportsValue.value

    if (chosen === antiFraudReportsValue.value) {
        return
    }

    if (chosen === 'yes') {
        userAutoEsoFlowRef.value?.openEnable()
    } else {
        userAutoEsoFlowRef.value?.openDisable()
    }
}

const form = useForm({
    minimum_credit_score: props.rule.minimum_credit_score ?? '',
    rent_income_multiplier: props.rule.rent_income_multiplier || '',
})

function submit() {
    form.put(route('screening-rules.update'))
}
</script>
